/**
 * Kullanıcılar Modülü — Merkezi Rol + Yetki Yönetimi (spec msg-USR, 106 madde).
 *
 * 3 rol: Sistem Yöneticisi (admin) / Müdür (manager) / Kullanıcı (diğer legacy roller).
 * — Sistem Yöneticisi → dinamik full-access (backend'te de override).
 * — Müdür → manuel yetki matrisi.
 * — Kullanıcı → yalnız seçilen sayfalar + izin verilen action'lar; Edit/Delete YASAK.
 *
 * Backend endpoint'leri: /users, /users/permission-registry, /users/{id}/copy-permissions,
 * /users/{id}/active, /me/permissions.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Table, Btn, Modal, Input, Select, Badge, Card } from "@/components/UI";
import { Plus, Pencil, Trash2, Users as UsersIcon, ShieldCheck, Copy, CheckCircle, AlertTriangle, Search } from "lucide-react";

/* ============================================================================
 * Yardımcılar
 * ==========================================================================*/
// UI'daki 3 rol → backend rol map'i.
// admin ve manager backend'de aynı isimle var. "Kullanıcı" (UI) → backend'de
// yeni oluşturulanlar için "sales" (varsayılan alt-rol) olarak yazılır ama
// SPECial rule (msg-USR rule 12) backend'te enforce edilir — hangi legacy rol
// olursa olsun admin/manager değilse "Kullanıcı"dır ve Edit/Delete yapamaz.
const UI_ROLES = [
  { key: "admin",   label: "Sistem Yöneticisi", tone: "emerald", fullAccess: true },
  { key: "manager", label: "Müdür",              tone: "indigo",  fullAccess: false },
  { key: "user",    label: "Kullanıcı",          tone: "slate",   fullAccess: false, restricted: ["edit", "delete"] },
];
const UI_ROLE_KEYS = UI_ROLES.map(r => r.key);
// Backend legacy → UI rol (görüntüleme için).
const toUiRole = (backendRole) => (backendRole === "admin" ? "admin" : backendRole === "manager" ? "manager" : "user");
// UI rol → backend rol (create/update için). "user" seçildiğinde backend'e "sales" gönderiyoruz
// (mevcut business logic'i bozmamak için legacy alt-rollerden bir tanesi seçildi;
// USER-kısıtı zaten role_is_kullanici() ile enforce edilir).
const toBackendRole = (uiRole, currentBackendRole) => {
  if (uiRole === "admin") return "admin";
  if (uiRole === "manager") return "manager";
  // Preserve existing legacy role when downgrading from admin/manager to keep audit continuity.
  if (currentBackendRole && !["admin", "manager"].includes(currentBackendRole)) return currentBackendRole;
  return "sales";
};

/* ============================================================================
 * Ana Sayfa
 * ==========================================================================*/
export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [registry, setRegistry] = useState({ pages: [], actions: [], roles: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [flt, setFlt] = useState({ role: "", status: "" });

  const emptyForm = {
    username: "", name: "", job_title: "", password: "",
    ui_role: "user", active: true,
    module_permissions: {}, action_permissions: {},
    // iter304 — Sistem Yetkilisi: rolden bağımsız GMP UPDATE/DELETE yetkisi.
    system_authority: false,
  };
  const [f, setF] = useState(emptyForm);
  const [copyOpen, setCopyOpen] = useState(false);

  const load = async () => {
    const [u, reg] = await Promise.all([api.get("/users"), api.get("/users/permission-registry")]);
    setRows(u.filter(x => !x.is_deleted));
    setRegistry(reg);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(emptyForm); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setF({
      username: r.username || (r.email ? r.email.split("@")[0] : ""),
      name: r.name || "",
      job_title: r.job_title || "",
      password: "",
      ui_role: toUiRole(r.role),
      active: !!r.active,
      module_permissions: r.module_permissions || {},
      action_permissions: r.action_permissions || {},
      system_authority: !!r.system_authority,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!f.username?.trim()) return alert("Kullanıcı adı zorunlu");
    if (!f.name?.trim()) return alert("Personel adı zorunlu");
    if (!editing && !f.password) return alert("Şifre zorunlu");
    const backendRole = toBackendRole(f.ui_role, editing?.role);
    // USER rolü için Edit/Delete permission'larını UI'da da temizle.
    const actionPerms = { ...f.action_permissions };
    if (f.ui_role === "user") {
      Object.keys(actionPerms).forEach(k => {
        if (k.endsWith(".edit") || k.endsWith(".delete")) actionPerms[k] = false;
      });
    }
    const payload = {
      username: f.username.trim(),
      name: f.name.trim(),
      job_title: f.job_title || null,
      role: backendRole,
      active: f.active,
      module_permissions: f.module_permissions,
      action_permissions: actionPerms,
      // iter304 — Sistem Yetkilisi (rolden bağımsız GMP UPDATE/DELETE yetkisi).
      // Admin rolüne set etmenin anlamı yok — admin zaten tam yetkilidir.
      system_authority: f.ui_role === "admin" ? false : !!f.system_authority,
    };
    if (f.password) payload.password = f.password;
    try {
      if (editing) await api.put(`/users/${editing.id}`, payload);
      else await api.post("/users", { ...payload, password: f.password });
      setOpen(false); setEditing(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const del = async (r) => {
    if (!window.confirm(`${r.name} kullanıcısı arşive alınsın mı? (geçmiş işlem kayıtları korunur, oturum kapatılır)`)) return;
    try { await api.del(`/users/${r.id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };
  const toggleActive = async (r) => {
    const nextActive = !r.active;
    if (!window.confirm(`${r.name} ${nextActive ? "aktifleştirilsin" : "pasife alınsın"} mi?`)) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${r.id}/active`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("erp_token")}` },
        body: JSON.stringify({ active: nextActive }),
      }).then(async res => {
        if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Hata"); }
        load();
      });
    } catch (e) { alert(e.message); }
  };

  // Filtered rows
  const filtered = useMemo(() => {
    let out = rows.slice();
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(r =>
        (r.username || "").toLowerCase().includes(q) ||
        (r.name || "").toLowerCase().includes(q) ||
        (r.job_title || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q),
      );
    }
    if (flt.role) out = out.filter(r => toUiRole(r.role) === flt.role);
    if (flt.status) out = out.filter(r => (r.active ? "active" : "passive") === flt.status);
    return out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [rows, search, flt]);

  return (
    <div>
      <PageHeader
        title="Kullanıcılar"
        description="Merkezi rol + sayfa + işlem yetkilendirme sistemi"
        actions={<Btn onClick={openNew} data-testid="new-user"><Plus size={14}/> Yeni Kullanıcı</Btn>}
      />

      <Card className="p-3 mb-4 bg-slate-50/70 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="md:col-span-2 relative">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Kullanıcı Ara</label>
            <div className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded bg-white">
              <Search size={14} className="text-slate-400"/>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Kullanıcı adı, personel, görev, e-posta..." className="flex-1 text-xs outline-none" data-testid="user-search"/>
            </div>
          </div>
          <Select label="Rol" value={flt.role} onChange={e => setFlt({ ...flt, role: e.target.value })} data-testid="flt-role">
            <option value="">Tümü</option>
            {UI_ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
          </Select>
          <Select label="Durum" value={flt.status} onChange={e => setFlt({ ...flt, status: e.target.value })} data-testid="flt-status">
            <option value="">Tümü</option>
            <option value="active">Aktif</option>
            <option value="passive">Pasif</option>
          </Select>
        </div>
      </Card>

      <Table testid="users-table" rows={filtered} columns={[
        { label: "Kullanıcı Adı", render: r => <span className="font-mono text-xs">{r.username || (r.email ? r.email.split("@")[0] : "—")}</span> },
        { label: "Personel Adı", render: r => <span className="font-medium">{r.name}</span> },
        { label: "Rol", render: r => {
          const ui = UI_ROLES.find(u => u.key === toUiRole(r.role));
          return (
            <div className="flex flex-col gap-0.5">
              <span className={`inline-block text-[11px] font-semibold border rounded px-2 py-0.5 ${ui?.tone === "emerald" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : ui?.tone === "indigo" ? "bg-indigo-100 text-indigo-800 border-indigo-300" : "bg-slate-100 text-slate-800 border-slate-300"}`} data-testid={`role-badge-${r.id}`}>{ui?.label || r.role}</span>
              {r.system_authority && r.role !== "admin" && (
                <span className="inline-block text-[10px] font-semibold border rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 border-amber-300 w-fit" data-testid={`sysauth-badge-${r.id}`} title="Sistem Yetkilisi — GMP UPDATE/DELETE açık">
                  ★ Sistem Yetkilisi
                </span>
              )}
            </div>
          );
        } },
        { label: "Görevi", render: r => r.job_title || <span className="text-slate-400 italic">—</span> },
        { label: "Durum", render: r => (
          <button onClick={() => toggleActive(r)} disabled={r.id === currentUser?.id}
            className={`inline-block text-[11px] font-semibold border rounded px-2 py-0.5 ${r.active ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200" : "bg-slate-200 text-slate-700 border-slate-400 hover:bg-slate-300"} disabled:opacity-50 disabled:cursor-not-allowed`}
            title={r.id === currentUser?.id ? "Kendi durumunuzu değiştiremezsiniz" : "Aktif/Pasif değiştir"}
            data-testid={`toggle-active-${r.id}`}>
            {r.active ? "● Aktif" : "○ Pasif"}
          </button>
        ) },
        { label: "İşlem", render: r => (
          <div className="flex gap-1">
            <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`edit-user-${r.id}`}><Pencil size={12}/> Düzenle</Btn>
            <Btn size="sm" variant="danger" onClick={() => del(r)} disabled={r.id === currentUser?.id} data-testid={`delete-user-${r.id}`}><Trash2 size={12}/></Btn>
          </div>
        ) },
      ]}/>
      {filtered.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">Filtreye uygun kullanıcı yok.</div>}

      {/* ============ Yeni / Düzenle Modal ============ */}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Kullanıcı Düzenle — ${editing.name}` : "Yeni Kullanıcı"} size="full">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <Input label="Kullanıcı Adı *" value={f.username} onChange={e => setF({ ...f, username: e.target.value })} disabled={!!editing} data-testid="user-username" placeholder="ör. mehmet"/>
          <Input label="Personel Adı Soyadı *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="user-name" placeholder="Ör. Mehmet Yılmaz"/>
          <Input label="Görevi" value={f.job_title} onChange={e => setF({ ...f, job_title: e.target.value })} data-testid="user-job" placeholder="Ör. Üretim Sorumlusu"/>
          <Input label={editing ? "Yeni Şifre (boş = değişmez)" : "Şifre *"} type="password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })} data-testid="user-password"/>
          <Select label="Rol *" value={f.ui_role} onChange={e => setF({ ...f, ui_role: e.target.value })} data-testid="user-role">
            {UI_ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
          </Select>
          <Select label="Durum" value={f.active ? "1" : "0"} onChange={e => setF({ ...f, active: e.target.value === "1" })} data-testid="user-status">
            <option value="1">Aktif</option><option value="0">Pasif</option>
          </Select>
        </div>

        {/* ---- Sistem Yöneticisi ise permission bölümü kapalı ---- */}
        {f.ui_role === "admin" && (
          <Card className="p-4 bg-emerald-50 border-emerald-200 mb-4">
            <div className="flex items-center gap-2 text-emerald-800">
              <ShieldCheck size={16}/>
              <div>
                <div className="text-sm font-bold">Sistem Yöneticisi — Tam Yetki</div>
                <div className="text-xs">Bu kullanıcı tüm sayfalara, işlemlere ve gelecekte eklenen tüm modüllere otomatik erişebilir. Ayrı yetki seçimi gerekmez.</div>
              </div>
            </div>
          </Card>
        )}

        {/* ---- Müdür / Kullanıcı için Yetki Matrisi ---- */}
        {f.ui_role !== "admin" && (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <ShieldCheck size={14}/> Yetki Matrisi
                {f.ui_role === "user" && (
                  <span className="text-[10px] font-normal text-amber-700 bg-amber-50 border border-amber-300 rounded px-2 py-0.5">
                    ⚠ Kullanıcı rolü Edit/Delete yapamaz
                  </span>
                )}
              </div>
              <Btn size="sm" variant="secondary" onClick={() => setCopyOpen(true)} data-testid="copy-permissions"><Copy size={12}/> Yetki Kopyala</Btn>
            </div>

            {/* iter304 — Sistem Yetkilisi flag'i (rolden bağımsız GMP UPDATE/DELETE yetkisi) */}
            {/* CustomerERP Faz 0: GMP'ye özel "Sistem Yetkilisi" seçeneği gizli */}
            <Card className="hidden p-3 mb-3 bg-amber-50/70 border-amber-300">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="user-system-authority-wrap">
                <input
                  type="checkbox"
                  checked={!!f.system_authority}
                  onChange={e => setF({ ...f, system_authority: e.target.checked })}
                  className="mt-1"
                  data-testid="user-system-authority"
                />
                <div className="flex-1">
                  <div className="text-sm font-bold text-amber-900 flex items-center gap-2">
                    <ShieldCheck size={14}/> Sistem Yetkilisi
                  </div>
                  <div className="text-[11px] text-amber-800 mt-0.5">
                    Rolden bağımsız yetki. Açık olduğunda kullanıcı <b>GMP Portalı</b> kayıtlarında
                    <b> düzenleme (UPDATE)</b> ve <b>silme (DELETE)</b> yapabilir, iş akışı
                    (onay/kapatma/revizyon) işlemlerini tetikleyebilir. Kapalıysa yalnızca GMP
                    modülü açıksa READ + CREATE (yeni kayıt / belge yükleme) yapabilir.
                  </div>
                </div>
              </label>
            </Card>

            <PermissionMatrix
              registry={registry}
              targetRole={f.ui_role}
              modulePerms={f.module_permissions}
              actionPerms={f.action_permissions}
              onChange={(mp, ap) => setF({ ...f, module_permissions: mp, action_permissions: ap })}
            />
          </>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="save-user"><CheckCircle size={14}/> Kaydet</Btn>
        </div>

        {/* ---- Yetki Kopyala Modal ---- */}
        <CopyPermissionsModal
          open={copyOpen}
          onClose={() => setCopyOpen(false)}
          users={rows.filter(r => r.id !== editing?.id)}
          targetRole={f.ui_role}
          onApply={(sourceUser) => {
            let mp = { ...(sourceUser.module_permissions || {}) };
            let ap = { ...(sourceUser.action_permissions || {}) };
            // Rol kısıtı: USER rolüne edit/delete asla kopyalanmaz (spec rule 58).
            if (f.ui_role === "user") {
              Object.keys(ap).forEach(k => { if (k.endsWith(".edit") || k.endsWith(".delete")) ap[k] = false; });
            }
            // Sistem Admin kaynak ise implicit full-access explicit hale gelmez;
            // sadece kaynağın kayıtlı override'ı taşınır.
            setF({ ...f, module_permissions: mp, action_permissions: ap });
            setCopyOpen(false);
          }}
        />
      </Modal>
    </div>
  );
}

/* ============================================================================
 * Yetki Matrisi Bileşeni
 * ==========================================================================*/
function PermissionMatrix({ registry, targetRole, modulePerms, actionPerms, onChange }) {
  const { pages = [], actions = [] } = registry || {};
  // Sayfa Görüntüle → çocuk action'lar (aynı page.key ile).
  const grouped = useMemo(() => {
    const byGroup = {};
    pages.forEach(p => { (byGroup[p.group] ||= []).push(p); });
    return byGroup;
  }, [pages]);

  const setPageView = (pageKey, val) => {
    const mp = { ...modulePerms, [pageKey]: val };
    let ap = { ...actionPerms };
    if (!val) {
      // Parent kapatıldığında child action'lar da kapanır (spec rule 15).
      actions.filter(a => a.page === pageKey).forEach(a => { ap[a.key] = false; });
    }
    onChange(mp, ap);
  };
  const setAction = (actionKey, val) => {
    // USER role için edit/delete asla açılamaz (spec rule 12).
    if (targetRole === "user" && (actionKey.endsWith(".edit") || actionKey.endsWith(".delete"))) return;
    const ap = { ...actionPerms, [actionKey]: val };
    onChange(modulePerms, ap);
  };

  const bulkAllPages = () => {
    const mp = {}; pages.forEach(p => { mp[p.key] = true; });
    onChange(mp, actionPerms);
  };
  const bulkNonePages = () => onChange({}, {});

  return (
    <Card className="p-3 bg-slate-50/50 border-slate-200 mb-2" data-testid="permission-matrix">
      <div className="flex justify-between items-center mb-2">
        <div className="text-xs text-slate-600">Sayfaya erişim = <b>Görüntüle</b>. Kapatınca alt action'lar da kapanır.</div>
        <div className="flex gap-1">
          <Btn size="sm" variant="secondary" onClick={bulkAllPages} data-testid="perm-all">Tüm Sayfalar</Btn>
          <Btn size="sm" variant="secondary" onClick={bulkNonePages} data-testid="perm-none">Temizle</Btn>
        </div>
      </div>
      <div className="space-y-3">
        {Object.entries(grouped).map(([grp, gpages]) => (
          <div key={grp}>
            <div className="text-[10px] font-bold uppercase text-slate-500 tracking-wide mb-1">{grp}</div>
            <div className="grid grid-cols-1 gap-1">
              {gpages.map(p => {
                const pageActions = actions.filter(a => a.page === p.key);
                const enabled = !!modulePerms?.[p.key];
                return (
                  <div key={p.key} className={`bg-white border rounded p-2 ${enabled ? "border-indigo-200" : "border-slate-200"}`}>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer flex-1">
                        <input type="checkbox" checked={enabled} onChange={e => setPageView(p.key, e.target.checked)} data-testid={`perm-page-${p.key}`}/>
                        <span>{p.label}</span>
                        <span className="text-[10px] font-mono text-slate-400">{p.key}</span>
                      </label>
                    </div>
                    {enabled && pageActions.length > 0 && (
                      <div className="mt-2 pl-6 flex flex-wrap gap-2">
                        {pageActions.map(a => {
                          const isEditDel = a.key.endsWith(".edit") || a.key.endsWith(".delete");
                          const forbidden = targetRole === "user" && isEditDel;
                          return (
                            <label key={a.key} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${forbidden ? "bg-slate-100 text-slate-400 border-slate-200 line-through" : "bg-slate-50 border-slate-300 hover:bg-slate-100 cursor-pointer"}`} title={forbidden ? "Kullanıcı rolü bu işlemi yapamaz" : a.key}>
                              <input type="checkbox" disabled={forbidden}
                                checked={!!actionPerms?.[a.key] && !forbidden}
                                onChange={e => setAction(a.key, e.target.checked)}
                                data-testid={`perm-action-${a.key}`}/>
                              <span>{a.label}</span>
                              {forbidden && <AlertTriangle size={10} className="text-amber-500"/>}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ============================================================================
 * Yetki Kopyala Modal (preview + apply)
 * ==========================================================================*/
function CopyPermissionsModal({ open, onClose, users, targetRole, onApply }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  useEffect(() => { if (!open) { setSelected(null); setQ(""); } }, [open]);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    return users.filter(u =>
      !s ||
      (u.name || "").toLowerCase().includes(s) ||
      (u.username || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s),
    ).slice(0, 40);
  }, [users, q]);

  const previewMP = selected?.module_permissions || {};
  const previewAP = selected?.action_permissions || {};
  const filteredAP = useMemo(() => {
    const ap = { ...previewAP };
    if (targetRole === "user") {
      Object.keys(ap).forEach(k => { if (k.endsWith(".edit") || k.endsWith(".delete")) ap[k] = false; });
    }
    return ap;
  }, [previewAP, targetRole]);

  return (
    <Modal open={open} onClose={onClose} title="Yetki Kopyala" size="lg">
      <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2 mb-3 text-indigo-900">
        <b>Yetkilerini kopyalayacağınız kullanıcı</b>u seçin. Rol kısıtları kopyalama esnasında otomatik uygulanır — örn. Müdür'den Kullanıcı'ya kopyada Edit/Delete taşınmaz.
      </div>
      <div className="mb-3">
        <label className="block text-xs font-semibold text-slate-500 mb-1">Ara (kullanıcı adı / personel adı)</label>
        <div className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded bg-white">
          <Search size={14} className="text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Yaz..." className="flex-1 text-xs outline-none" data-testid="copy-search" autoFocus/>
        </div>
      </div>
      <div className="max-h-40 overflow-y-auto border border-slate-200 rounded mb-3" data-testid="copy-source-list">
        {matches.length === 0 && <div className="p-3 text-xs italic text-slate-400">Eşleşme yok</div>}
        {matches.map(u => (
          <button key={u.id} onClick={() => setSelected(u)}
            className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-0 ${selected?.id === u.id ? "bg-indigo-50 border-l-4 border-l-indigo-500" : "hover:bg-slate-50"}`}
            data-testid={`copy-source-${u.id}`}>
            <div className="font-semibold">{u.name} <span className="text-slate-400">·</span> <span className="font-mono">{u.username || (u.email || "").split("@")[0]}</span> <span className="text-slate-400">·</span> <span className="italic">{UI_ROLES.find(r => r.key === toUiRole(u.role))?.label || u.role}</span></div>
            {u.job_title && <div className="text-[10px] text-slate-500">{u.job_title}</div>}
          </button>
        ))}
      </div>
      {selected && (
        <>
          <div className="text-xs font-bold uppercase text-slate-500 mb-1">Kopyalanacak (Önizleme)</div>
          <div className="max-h-40 overflow-y-auto bg-slate-50 border border-slate-200 rounded p-2 mb-3 text-[11px]" data-testid="copy-preview">
            <div><b>Sayfalar:</b> {Object.entries(previewMP).filter(([_, v]) => v).map(([k]) => k).join(", ") || <i className="text-slate-400">yok</i>}</div>
            <div className="mt-1"><b>İşlemler:</b> {Object.entries(filteredAP).filter(([_, v]) => v).map(([k]) => k).join(", ") || <i className="text-slate-400">yok</i>}</div>
            {targetRole === "user" && Object.keys(previewAP).some(k => (k.endsWith(".edit") || k.endsWith(".delete")) && previewAP[k]) && (
              <div className="mt-1 text-amber-700"><AlertTriangle size={10} className="inline"/> Kaynak'taki Edit/Delete yetkileri Kullanıcı rolüne kopyalanmayacak.</div>
            )}
          </div>
        </>
      )}
      <div className="flex justify-end gap-2">
        <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
        <Btn onClick={() => onApply(selected)} disabled={!selected} data-testid="copy-apply"><Copy size={14}/> Yetkileri Uygula</Btn>
      </div>
    </Modal>
  );
}
